import { IconButton, Skeleton, Stack, Typography } from '@mui/material';

import BoxIcon from '@/components/Icons/BoxIcon';
import CpuIcon from '@/components/Icons/CpuIcon';
import LinkIcon from '@/components/Icons/LinkIcon';
import SettingsIcon from '@/components/Icons/SettingsIcon';
import ToolIcon from '@/components/Icons/ToolIcon';
import { Tabs } from '@/pages/TeamSettings/types';
import { BasetenPageEnum } from '@/routes';
import TeamMenu from '@/sections/Teams/TeamMenu';
import { useSelectedTeamId, useTeam } from '@/sections/Teams/hooks';

import { useWorkspaceSection } from '../hooks';
import { AppLink, MenuItem, Section, TeamResource, TeamResourceName } from '../styled';
import { WorkspaceSections } from '../types';

function Teams() {
  const selectedSection = useWorkspaceSection();
  const { selectedTeamId, loading } = useSelectedTeamId();

  const { team, loading: teamLoading } = useTeam(selectedTeamId, true);

  const isLoading = loading || teamLoading;

  return (
    <Section gap={2}>
      <Stack gap={1.5}>
        <Typography variant="h6" color="text.muted">
          Teams
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <TeamMenu />
          <IconButton sx={{ p: 0, mr: -0.5 }}>
            <AppLink
              {...(selectedTeamId && {
                page: BasetenPageEnum.TeamSettings,
                params: { teamId: selectedTeamId, tabId: Tabs.General },
              })}
            >
              <SettingsIcon color="secondary" />
            </AppLink>
          </IconButton>
        </Stack>
      </Stack>
      <Stack gap={0.5}>
        <MenuItem selected={selectedSection === WorkspaceSections.Models}>
          <AppLink
            {...(selectedTeamId && {
              page: BasetenPageEnum.WorkspaceTeams,
              params: {
                section: WorkspaceSections.Models,
                teamId: selectedTeamId,
              },
            })}
          >
            <TeamResource>
              <TeamResourceName>
                <CpuIcon
                  color={selectedSection === WorkspaceSections.Models ? 'inherit' : 'secondary'}
                />{' '}
                Models
              </TeamResourceName>
              {isLoading ? (
                <Skeleton variant="text" width={40} />
              ) : (
                <Typography variant="h6" color="text.subtle">
                  {team?.oraclesCount || 0}
                </Typography>
              )}
            </TeamResource>
          </AppLink>
        </MenuItem>
        <MenuItem selected={selectedSection === WorkspaceSections.Chains}>
          <AppLink
            {...(selectedTeamId && {
              page: BasetenPageEnum.WorkspaceTeams,
              params: {
                section: WorkspaceSections.Chains,
                teamId: selectedTeamId,
              },
            })}
          >
            <TeamResource>
              <TeamResourceName>
                <LinkIcon
                  color={selectedSection === WorkspaceSections.Chains ? 'inherit' : 'secondary'}
                />
                Chains
              </TeamResourceName>
              {isLoading ? (
                <Skeleton variant="text" width={40} />
              ) : (
                <Typography variant="h6" color="text.subtle">
                  {team?.chainsCount || 0}
                </Typography>
              )}
            </TeamResource>
          </AppLink>
        </MenuItem>
        <MenuItem selected={selectedSection === WorkspaceSections.ModelAPIs}>
          <AppLink
            {...(selectedTeamId && {
              page: BasetenPageEnum.WorkspaceTeams,
              params: {
                section: WorkspaceSections.ModelAPIs,
                teamId: selectedTeamId,
              },
            })}
          >
            <TeamResource>
              <TeamResourceName>
                <BoxIcon
                  color={selectedSection === WorkspaceSections.ModelAPIs ? 'inherit' : 'secondary'}
                />{' '}
                Model APIs
              </TeamResourceName>
              {isLoading ? (
                <Skeleton variant="text" width={40} />
              ) : (
                <Typography variant="h6" color="text.subtle">
                  {team?.modelsApisCount || 0}
                </Typography>
              )}
            </TeamResource>
          </AppLink>
        </MenuItem>
        <MenuItem selected={selectedSection === WorkspaceSections.Training}>
          <AppLink
            {...(selectedTeamId && {
              page: BasetenPageEnum.WorkspaceTeams,
              params: {
                section: WorkspaceSections.Training,
                teamId: selectedTeamId,
              },
            })}
          >
            <TeamResource>
              <TeamResourceName>
                <ToolIcon
                  color={selectedSection === WorkspaceSections.Training ? 'inherit' : 'secondary'}
                />{' '}
                Training
              </TeamResourceName>
              {isLoading ? (
                <Skeleton variant="text" width={40} />
              ) : (
                <Typography variant="h6" color="text.subtle">
                  {team?.trainingProjectsCount || 0}
                </Typography>
              )}
            </TeamResource>
          </AppLink>
        </MenuItem>
      </Stack>
    </Section>
  );
}

export default Teams;
